import { useCallback } from "react";

import type { BoardSource } from "@/features/automations/board/BoardEntry";

import { useCaseIde } from "../../context/CaseIdeContext";
import { WORKFLOWS_TAB_TITLE } from "../../openArtifacts";
import { artifactKey, type Artifact } from "../../types";

export const workflowsArtifact = (individualId: string): Artifact => ({
  kind: "workflows",
  id: individualId,
  title: WORKFLOWS_TAB_TITLE
});

/**
 * Open (or focus) the center pane's Case Automations tab.
 *
 * Which side it shows is the caller's to say: `openPrepare` lands on Let
 * CaseBlink Prepare, `openAutomations` on Run Your Automations, and plain
 * `open` states no intent, so the tab keeps its own default (case preparation)
 * or, if it is already open, the attorney's choice there. Entry points that
 * mean case preparation use `openPrepare` so they switch an open tab too.
 */
export const useOpenWorkflowsTab = () => {
  const { open, individualId, activeKey, requestWorkflowsEntry } = useCaseIde();
  const artifact = workflowsArtifact(individualId);
  const key = artifactKey(artifact);
  const openAt = useCallback(
    (source: BoardSource | null) => {
      requestWorkflowsEntry(source);
      open(workflowsArtifact(individualId));
    },
    [open, individualId, requestWorkflowsEntry]
  );
  const openTab = useCallback(() => openAt(null), [openAt]);
  const openPrepare = useCallback(() => openAt("caseblink"), [openAt]);
  const openAutomations = useCallback(() => openAt("firm"), [openAt]);
  return {
    open: openTab,
    openPrepare,
    openAutomations,
    active: activeKey === key
  };
};
